/**
 * One IndexedDB database for everything the app keeps on the device:
 *   kv        auth data, device id, small settings
 *   outbox    entries / voids waiting for the server (Requirements §9.3)
 *   items, uom_groups, barcodes   master cache for offline counting (§9.2)
 * Writes wait for the transaction to COMPLETE (durability "strict"), so a saved entry survives
 * a refresh, a killed tab or a browser restart.
 */
export const DB_NAME = 'bnn-inventory';
const DB_VERSION = 2;

export type StoreName = 'kv' | 'outbox' | 'items' | 'uom_groups' | 'barcodes';

let dbPromise: Promise<IDBDatabase> | null = null;

export function openDb(): Promise<IDBDatabase> {
  dbPromise ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('kv')) db.createObjectStore('kv');
      if (!db.objectStoreNames.contains('outbox')) db.createObjectStore('outbox', { keyPath: 'key' }).createIndex('session_id', 'session_id');
      if (!db.objectStoreNames.contains('items')) db.createObjectStore('items', { keyPath: 'item_code' });
      if (!db.objectStoreNames.contains('uom_groups')) db.createObjectStore('uom_groups', { keyPath: 'uom_group_code' });
      if (!db.objectStoreNames.contains('barcodes')) db.createObjectStore('barcodes', { keyPath: 'barcode' }).createIndex('item_code', 'item_code');
    };
    req.onsuccess = () => {
      const db = req.result;
      db.onversionchange = () => db.close(); // another tab upgraded: let it proceed
      resolve(db);
    };
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('IndexedDB blocked by another tab'));
  });
  dbPromise.catch(() => {
    dbPromise = null;
  });
  return dbPromise;
}

/** For tests: forget the open connection. */
export async function closeDb() {
  const p = dbPromise;
  dbPromise = null;
  if (p) (await p.catch(() => null))?.close();
}

const done = (tx: IDBTransaction) =>
  new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error('transaction aborted'));
  });

const result = <T>(req: IDBRequest<T>) =>
  new Promise<T>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

function tx(db: IDBDatabase, store: StoreName, mode: IDBTransactionMode) {
  // "strict" = data is flushed to disk before oncomplete fires (Chrome); others ignore the hint.
  return db.transaction(store, mode, { durability: mode === 'readwrite' ? 'strict' : 'default' } as IDBTransactionOptions);
}

export async function idbGet<T>(store: StoreName, key: IDBValidKey): Promise<T | undefined> {
  const db = await openDb();
  return result(tx(db, store, 'readonly').objectStore(store).get(key)) as Promise<T | undefined>;
}

export async function idbGetAll<T>(store: StoreName): Promise<T[]> {
  const db = await openDb();
  return result(tx(db, store, 'readonly').objectStore(store).getAll()) as Promise<T[]>;
}

export async function idbGetAllByIndex<T>(store: StoreName, index: string, key: IDBValidKey): Promise<T[]> {
  const db = await openDb();
  return result(tx(db, store, 'readonly').objectStore(store).index(index).getAll(key)) as Promise<T[]>;
}

/** Put rows (keyPath stores) and resolve only after the transaction committed. */
export async function idbPutMany(store: StoreName, values: unknown[]): Promise<void> {
  if (!values.length) return;
  const db = await openDb();
  const t = tx(db, store, 'readwrite');
  const s = t.objectStore(store);
  for (const v of values) s.put(v);
  await done(t);
}

/** Put one value under an explicit key (kv store). */
export async function idbSet(store: StoreName, key: IDBValidKey, value: unknown): Promise<void> {
  const db = await openDb();
  const t = tx(db, store, 'readwrite');
  t.objectStore(store).put(value, key);
  await done(t);
}

export async function idbDelete(store: StoreName, keys: IDBValidKey[]): Promise<void> {
  if (!keys.length) return;
  const db = await openDb();
  const t = tx(db, store, 'readwrite');
  for (const k of keys) t.objectStore(store).delete(k);
  await done(t);
}

export async function idbClear(store: StoreName): Promise<void> {
  const db = await openDb();
  const t = tx(db, store, 'readwrite');
  t.objectStore(store).clear();
  await done(t);
}

/** Ask the browser not to evict our data under storage pressure (Requirements §9.4). */
export async function requestPersistentStorage(): Promise<boolean> {
  try {
    if (!navigator.storage?.persist) return false;
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}
